import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { from, switchMap, catchError, throwError } from 'rxjs';
import { AuthService } from '../keycloak/services/auth.service';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';

/**
 * Interceptor funcional encargado de la gestión de credenciales y seguridad en las comunicaciones HTTP.
 * * * Este interceptor actúa como un middleware en el pipeline de {@link HttpClient}. 
 * Su función principal es interceptar las peticiones salientes para inyectar tokens de identidad
 * y gestionar de forma centralizada las respuestas de error relacionadas con la autorización.
 * * * ### Funcionalidades Core:
 * 1. **Inyección de JWT:** Recupera de forma asíncrona el ID Token y lo añade al header `Authorization`.
 * 2. **Seguridad CSRF/Sesión:** Habilita `withCredentials` para el transporte de cookies seguras.
 * 3. **Gestión de Errores de Red:** Captura códigos de estado críticos (401, 403, 404) para orquestar la navegación.
 * * * @param req - La petición saliente original (inmutable).
 * @param next - El siguiente manejador en la cadena de interceptores.
 * * * @returns Un flujo de eventos HTTP {@link Observable} con la petición procesada.
 * * * @see {@link AuthService}
 * @see {@link HttpInterceptorFn}
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {

  /** Inyección de dependencias mediante el contexto de ejecución funcional. */
  const authService = inject(AuthService);
  const router = inject(Router);

  // Definimos qué URLs queremos interceptar (usualmente desde tu environment)
  const targetServiceUrl = environment.auth_environment.BFF_BACKEND_SERVICE_USER_CONTEXT;

  /**
   * Pipeline de procesamiento de la petición.
   * * Se utiliza `from()` para transformar la promesa de obtención del token en un observable,
   * permitiendo su integración nativa con los operadores de RxJS.
   */
  return from(authService.getIdToken()).pipe(

    /**
     * Proyecta el token obtenido en una nueva petición HTTP.
     * * @param token - Representa el JWT (JSON Web Token) obtenido del proveedor de identidad.
     */
    switchMap(token => {

      // Continuidad de la petición si no se dispone de una sesión activa.
      if (!token) {
        return next(req);
      }

      /**
       * Creación de una copia modificada de la petición.
       * * Nota: Las peticiones en Angular son inmutables por diseño; para añadir headers,
       * se debe clonar la instancia original.
       */
      const authReq = req.clone({
        setHeaders: {
          'x-app-code': environment.APP_CODE,
          Authorization: `Bearer ${token}`
        },
        /** Habilita el intercambio de cookies para entornos con Cross-Site Request Forgery (CSRF) protection. */
        withCredentials: true
      });

      /**
       * Ejecución de la petición clonada y monitorización de la respuesta.
       */
      return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {

          // 401/403 fuera del BFF: redirige a unauthorized y corta
          if ((error.status === 401 || error.status === 403) && !req.url.includes(targetServiceUrl)) {
            console.error(`[Security Alert] Acceso no autorizado. Status: ${error.status}`);
            router.navigate([environment.auth_environment.REDIRECT_UNAUTHORIZED_RUTE]);
            return throwError(() => error);
          }

          // Errores dentro del BFF de autorización
          if (req.url.includes(targetServiceUrl)) {
            if (error.status === 401 || error.status === 403) {
              console.error(`[Security Alert] Acceso no autorizado en BFF. Status: ${error.status}`);
              router.navigate([environment.auth_environment.REDIRECT_UNAUTHORIZED_RUTE]);
            } else if (error.status === 404) {
              console.info('[Response Alert] Recurso no encontrado en BFF (404).');
              router.navigate([environment.auth_environment.REDIRECT_ERROR_AUTHORIZATION_SERVICE_RUTE]);
            } else {
              console.error(`[Security Alert] Error en BFF. Status: ${error.status}`);
              router.navigate([environment.auth_environment.REDIRECT_ERROR_AUTHORIZATION_SERVICE_RUTE]);
            }
          }

          return throwError(() => error);
        })
      );
    })
  );
};